import { useMutation } from "@apollo/client"
import { yupResolver } from "@hookform/resolvers/yup"
import React, { useCallback, useState } from "react"
import { useForm, type SubmitHandler } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import Shell from "./shell"
import { graphql } from "@/api"
import FormForm from "@/common/components/form_form"
import FormInput from "@/common/components/form_input"
import { Routemap } from "@/common/routes"
import yup from "@/common/yup"

const passwordResetDoc = graphql(`
  mutation PasswordReset($email: String!) {
    passwordReset(email: $email) {
      message

      errors {
        message
      }
    }
  }
`)

const passwordChangeDoc = graphql(`
  mutation PasswordChange($token: String, $tempPassword: String!, $tempPasswordConfirmation: String!) {
    passwordChange(token: $token, tempPassword: $tempPassword, tempPasswordConfirmation: $tempPasswordConfirmation) {
      success
      message

      errors {
        message
      }
    }
  }
`)

interface ChangePasswordFormProps {
  customer: string
  token?: string
}

interface ChangePasswordValues {
  password: string
  passwordConfirmation: string
}

const newPasswordSchema = yup.object().shape({
  password: yup.string().required(),
  passwordConfirmation: yup.string().required(),
})

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({ customer, token }) => {
  const { t } = useTranslation()
  const [mutation, result] = useMutation(passwordChangeDoc)
  const formMethods = useForm<ChangePasswordValues>({ resolver: yupResolver(newPasswordSchema) })
  const { handleSubmit } = formMethods
  const [error, setError] = useState<string>()
  const [message, setMessage] = useState<string>()

  const submit: SubmitHandler<ChangePasswordValues> = useCallback(
    async (form, e) => {
      e?.preventDefault()

      const result = await mutation({
        variables: { token, tempPassword: form.password, tempPasswordConfirmation: form.passwordConfirmation },
      })
      const data = result.data?.passwordChange

      if (data) {
        const errors = data.errors || []

        if (errors[0] && errors.length > 0) {
          setError(errors[0].message)
        } else if (data.message) {
          setError(undefined)
          setMessage(data.message)
        }
      }
    },
    [mutation, token]
  )

  return (
    <>
      {error && <p className="form_message bg-error">{error}</p>}
      {message && <p className="form_message bg-success">{message}</p>}
      <p className="paragraph-sm">{t("customer.choose_password")}</p>
      <p className="paragraph-2xs">{t("customer.id")}:</p>
      <p className="paragraph-sm data-hj-suppress">{customer}</p>
      <FormForm id="forgot_password" onSubmit={handleSubmit(submit)} formMethods={formMethods} className="max-w-lg">
        {/* for password managers */}
        <input type="text" name="customer_email" defaultValue={customer} style={{ display: "none" }} />
        <FormInput
          type="password"
          name="password"
          id="customer_temp_password"
          autoComplete="off"
          className="form-control data-hj-suppress"
          placeholder={`${t("customer.new_password")}*`}
          rules={{ required: true }}
        />
        <FormInput
          type="password"
          name="passwordConfirmation"
          id="customer_temp_password_confirmation"
          autoComplete="off"
          className="form-control data-hj-suppress"
          placeholder={`${t("customer.repeat_new_password")}*`}
          rules={{ required: true }}
        />
        <button id="commit" className="button button-primary button-medium" type="submit" disabled={result.loading}>
          {t("misc.update")}
        </button>
        <Link to={Routemap.customerZoneProfile(t)} className="button button-secondary button-medium">
          {t("misc.cancel")}
        </Link>
      </FormForm>
    </>
  )
}

interface ForgotPasswordValues {
  email: string
}

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation()
  const { register, handleSubmit } = useForm<ForgotPasswordValues>()
  const [mutation, result] = useMutation(passwordResetDoc)
  const [error, setError] = useState<string>()
  const [message, setMessage] = useState<string>()

  const submit: SubmitHandler<ForgotPasswordValues> = useCallback(
    async (form, e) => {
      e?.preventDefault()

      const result = await mutation({ variables: { email: form.email } })
      const data = result.data?.passwordReset

      if (data) {
        const errors = data.errors || []

        if (errors[0] && errors.length > 0) {
          setError(errors[0].message)
        } else if (data.message) {
          setError(undefined)
          setMessage(data.message)
        }
      }
    },
    [mutation]
  )

  return (
    <div id="retrieve_password">
      {error && <p className="form_message bg-error">{error}</p>}
      {message && <p className="form_message bg-info">{message}</p>}

      <form id="forgot_password" onSubmit={handleSubmit(submit)}>
        <div className="form-group">
          <label htmlFor="customer_forgot_email">{t("customer.email")}*</label>
          <input id="customer_forgot_email" className="form-control" {...register("email")} />
        </div>
        <button id="commit" className="button button-primary button-small" type="submit" disabled={result.loading}>
          {t("customer.send_password")}
        </button>
      </form>
    </div>
  )
}

interface ChangePasswordProps {
  customer?: string
  token?: string
}

const ChangePassword: React.FC<ChangePasswordProps> = ({ customer, token }) => {
  return (
    <Shell customer={customer} profile>
      <div className="customerarea password">
        <div className="row">
          <div className="col-lg-4 col-md-4 col-xs-12">
            {customer ? <ChangePasswordForm customer={customer} token={token} /> : <ForgotPassword />}
          </div>
        </div>
      </div>
    </Shell>
  )
}

export default ChangePassword
